import { z } from 'zod';
import { baseComponentSchema, buttonSchema } from '../component-base.schemas';

const htmlSplitBackgroundValueSchema = z
  .string()
  .refine(
    val =>
      /^#[0-9A-Fa-f]{6}$/.test(val) ||
      val.startsWith('linear-gradient') ||
      val.startsWith('radial-gradient'),
    { message: 'Must be a hex color or CSS gradient' }
  )
  .nullable()
  .optional();

export const htmlSplitCustomColorsSchema = z
  .object({
    background: htmlSplitBackgroundValueSchema,
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    tagline: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
  })
  .optional();

/** Nested embed — same shape as html-embed data (subset) */
export const htmlSplitEmbedSchema = z
  .object({
    embedType: z.enum(['custom', 'iframe', 'video', 'map', 'social', 'code']).default('custom'),
    custom: z
      .object({
        html: z.string().optional(),
        css: z.string().optional(),
        javascript: z.string().optional(),
        sanitize: z.boolean().optional(),
      })
      .optional(),
    iframe: z.record(z.any()).optional(),
    video: z.record(z.any()).optional(),
    responsive: z.boolean().optional(),
    lazyLoad: z.boolean().optional(),
  })
  .passthrough()
  .optional();

export const htmlSplitDataSchema = z
  .object({
    tagline: z.string().max(100).optional(),
    title: z.string().min(1, 'Title is required').max(200),
    subtitle: z.string().max(1000).optional(),
    buttons: z.array(buttonSchema).default([]),
    layout: z.enum(['html-left', 'html-right']).default('html-right'),
    alignment: z.enum(['left', 'center', 'right']).default('left'),
    size: z.enum(['small', 'medium', 'large']).default('large'),
    backgroundColor: htmlSplitBackgroundValueSchema,
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    htmlWidth: z.number().int().min(20).max(80).default(50),
    textWidth: z.number().int().min(20).max(80).default(50),
    stackOnMobile: z.boolean().default(true),
    mobileHtmlFirst: z.boolean().default(false),
    htmlMinHeight: z.number().int().min(200).max(2000).default(480),
    embed: htmlSplitEmbedSchema,
    customColors: htmlSplitCustomColorsSchema,
  })
  .refine(data => data.htmlWidth + data.textWidth === 100, {
    message: 'HTML width and text width must add up to 100%',
  });

export const htmlSplitComponentSchema = baseComponentSchema.extend({
  type: z.literal('html-split'),
  data: htmlSplitDataSchema,
});

export type HtmlSplitData = z.infer<typeof htmlSplitDataSchema>;
export type HtmlSplitComponent = z.infer<typeof htmlSplitComponentSchema>;
